import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { 
  FileText, 
  Upload, 
  Table, 
  PenLine,
  ArrowRight,
  CheckCircle,
  Sparkles,
  FolderOpen,
  FileSpreadsheet,
  Building2,
  ChevronRight,
  HelpCircle,
  FileCheck,
  X,
  CreditCard,
  Receipt,
  Calculator,
  AlertCircle,
  BookOpen
} from 'lucide-react';

type InputMethod = 'upload' | 'spreadsheet' | 'manual' | null;
type PLStatus = 'yes' | 'no' | null;

// Redirect to wizard - this page is deprecated but kept for backwards compatibility
function NewClaimRedirect() {
  const navigate = useNavigate();
  
  useEffect(() => {
    // Redirect to wizard
    navigate('/wizard-v2', { replace: true });
  }, [navigate]);
  
  return (
    <div className="min-h-screen flex items-center justify-center">
      <p className="text-gray-500">Redirecting to wizard...</p>
    </div>
  );
}

// Keep redirect available if needed
export { NewClaimRedirect };

// Document Guide Modal Component
function DocumentGuideModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  if (!isOpen) return null;

  const alternativeDocs = [
    { 
      icon: FileText, 
      name: 'Tax Returns (Form 1065, K-1)', 
      description: 'State and federal returns from the last 2-3 years verify income history',
      priority: 'high'
    },
    { 
      icon: CreditCard, 
      name: 'Bank Statements', 
      description: 'Provides objective cash flow data to trace revenues and expenses',
      priority: 'high'
    },
    { 
      icon: Receipt, 
      name: 'Sales & Revenue Reports', 
      description: 'Daily/monthly revenue records and customer orders show income patterns',
      priority: 'high'
    },
    { 
      icon: FileCheck, 
      name: 'Payroll Records', 
      description: 'Employee wage logs support continuing expense claims',
      priority: 'medium'
    },
    { 
      icon: FileText, 
      name: 'Invoices & Contracts', 
      description: 'Supplier contracts and customer orders prove lost opportunities',
      priority: 'medium'
    },
    { 
      icon: Calculator, 
      name: 'Budgets & Forecasts', 
      description: 'Pre-loss projections support "but-for" revenue estimates',
      priority: 'medium'
    },
  ];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        
        {/* Modal */}
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <BookOpen className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Filing Without P&L Statements</h2>
                <p className="text-sm text-gray-500">Alternative documentation guide</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Intro */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <p className="text-sm text-blue-800">
                <strong>Good news:</strong> If monthly P&L statements are unavailable, you can still file a 
                business interruption claim using alternative financial documents. Our AI-powered system 
                can <strong>reconstruct your P&L</strong> from bank statements and other records.
              </p>
            </div>

            {/* Alternative Documents */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Accepted Alternative Documents</h3>
              <div className="space-y-3">
                {alternativeDocs.map((doc, i) => (
                  <div key={i} className={`flex items-start gap-3 p-3 rounded-lg border ${
                    doc.priority === 'high' ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <div className={`p-2 rounded-lg ${
                      doc.priority === 'high' ? 'bg-green-100' : 'bg-gray-100'
                    }`}>
                      <doc.icon className={`w-4 h-4 ${
                        doc.priority === 'high' ? 'text-green-600' : 'text-gray-600'
                      }`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900">{doc.name}</p>
                        {doc.priority === 'high' && (
                          <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full font-medium">
                            Recommended
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{doc.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* How It Works */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">How P&L Reconstruction Works</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <span className="text-blue-600 font-bold">1</span>
                  </div>
                  <p className="text-sm font-medium text-gray-900">Upload Documents</p>
                  <p className="text-xs text-gray-500 mt-1">Bank statements, tax returns, receipts</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <span className="text-blue-600 font-bold">2</span>
                  </div>
                  <p className="text-sm font-medium text-gray-900">AI Classification</p>
                  <p className="text-xs text-gray-500 mt-1">Transactions auto-categorized to expense types</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <span className="text-blue-600 font-bold">3</span>
                  </div>
                  <p className="text-sm font-medium text-gray-900">P&L Generated</p>
                  <p className="text-xs text-gray-500 mt-1">Reconstructed statement validated against benchmarks</p>
                </div>
              </div>
            </div>

            {/* Industry Benchmarks Note */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-900">Industry Benchmark Validation</p>
                  <p className="text-sm text-amber-800 mt-1">
                    Your reconstructed P&L is automatically compared against industry averages (using BLS data) 
                    to validate COGS ratios, expense categories, and profit margins. This adds credibility to 
                    claims filed without traditional P&L statements.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3 rounded-b-2xl">
            <button 
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium"
            >
              Close
            </button>
            <button 
              onClick={onClose}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
            >
              Got it, let's continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// NewClaimLegacy - the full P&L question flow
function NewClaimLegacy() {
  const navigate = useNavigate();
  const [selectedMethod, setSelectedMethod] = useState<InputMethod>(null);
  const [hoveredMethod, setHoveredMethod] = useState<InputMethod>(null);
  const [plStatus, setPLStatus] = useState<PLStatus>(null);
  const [showDocGuide, setShowDocGuide] = useState(false);

  const methods = [
    {
      id: 'upload' as InputMethod,
      icon: Upload,
      title: 'Upload Documents',
      subtitle: 'Bank statements, tax docs, PDFs',
      description: 'Best for raw financial documents that need AI-powered cleanup and categorization.',
      descriptionNoPL: 'Perfect for P&L reconstruction from bank statements and tax documents.',
      features: [
        { icon: Sparkles, text: 'AI Transaction Categorization' },
        { icon: CheckCircle, text: 'Messiness Detection' },
        { icon: FolderOpen, text: 'P&L Reconstruction' },
      ],
      color: 'blue',
      recommended: true,
      recommendedNoPL: true,
      path: '/bulk-upload?mode=documents'
    },
    {
      id: 'spreadsheet' as InputMethod,
      icon: FileSpreadsheet,
      title: 'Import Spreadsheet',
      subtitle: 'CSV, Excel, QuickBooks exports',
      description: 'Best for pre-organized financial data that\'s already categorized.',
      descriptionNoPL: 'Use if you have transaction data exported from your accounting software.',
      features: [
        { icon: Table, text: 'Auto-detect columns' },
        { icon: CheckCircle, text: 'Data validation' },
        { icon: ArrowRight, text: 'Quick import' },
      ],
      color: 'emerald',
      recommended: false,
      recommendedNoPL: false,
      path: '/bulk-upload?mode=spreadsheet'
    },
    {
      id: 'manual' as InputMethod,
      icon: PenLine,
      title: 'Manual Entry',
      subtitle: 'Enter data directly in wizard',
      description: 'Best when you have summary financials ready or want full control.',
      descriptionNoPL: 'Enter reconstructed figures manually if you\'ve already calculated them.',
      features: [
        { icon: Building2, text: 'Industry templates' },
        { icon: CheckCircle, text: 'Guided process' },
        { icon: ArrowRight, text: 'Start immediately' },
      ],
      color: 'blue',
      recommended: false,
      recommendedNoPL: false,
      path: '/wizard-v2'
    }
  ];

  const getColorClasses = (color: string, isSelected: boolean, isHovered: boolean) => {
    const colors: Record<string, { bg: string; border: string; icon: string; text: string }> = {
      blue: {
        bg: isSelected ? 'bg-blue-50' : isHovered ? 'bg-blue-50/50' : 'bg-white',
        border: isSelected ? 'border-blue-500 ring-2 ring-blue-500/20' : 'border-gray-200 hover:border-blue-300',
        icon: 'bg-blue-100 text-blue-600',
        text: 'text-blue-600'
      },
      emerald: {
        bg: isSelected ? 'bg-emerald-50' : isHovered ? 'bg-emerald-50/50' : 'bg-white',
        border: isSelected ? 'border-emerald-500 ring-2 ring-emerald-500/20' : 'border-gray-200 hover:border-emerald-300',
        icon: 'bg-emerald-100 text-emerald-600',
        text: 'text-emerald-600'
      },
      violet: {
        bg: isSelected ? 'bg-violet-50' : isHovered ? 'bg-violet-50/50' : 'bg-white',
        border: isSelected ? 'border-violet-500 ring-2 ring-violet-500/20' : 'border-gray-200 hover:border-violet-300',
        icon: 'bg-violet-100 text-violet-600',
        text: 'text-violet-600'
      }
    };
    return colors[color];
  };

  const handleContinue = () => {
    const method = methods.find(m => m.id === selectedMethod);
    if (method) {
      // Pass P&L status to next step
      const path = plStatus === 'no' 
        ? `${method.path}${method.path.includes('?') ? '&' : '?'}noPL=true`
        : method.path;
      navigate(path);
    }
  };

  const isRecommended = (method: typeof methods[0]) => {
    if (plStatus === 'no') return method.recommendedNoPL;
    return method.recommended;
  };

  return (
    <AppLayout>
      <div className="p-6 lg:p-8 max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Create New Claim</h1>
          <p className="text-gray-500 mt-1">Let's gather your financial information</p>
        </div>

        {/* Step 1: P&L Status Question */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium">1</div>
            <h2 className="font-semibold text-gray-900">Do you have monthly Profit & Loss (P&L) statements?</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Yes Option */}
            <button
              onClick={() => { setPLStatus('yes'); setSelectedMethod(null); }}
              className={`p-4 rounded-xl border-2 text-left transition-all ${
                plStatus === 'yes' 
                  ? 'border-green-500 bg-green-50 ring-2 ring-green-500/20' 
                  : 'border-gray-200 hover:border-green-300 bg-white'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${plStatus === 'yes' ? 'bg-green-100' : 'bg-gray-100'}`}>
                  <CheckCircle className={`w-5 h-5 ${plStatus === 'yes' ? 'text-green-600' : 'text-gray-400'}`} />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Yes, I have P&L statements</p>
                  <p className="text-sm text-gray-500">Monthly or quarterly financial statements available</p>
                </div>
              </div>
            </button>

            {/* No Option */}
            <button
              onClick={() => { setPLStatus('no'); setSelectedMethod('upload'); }}
              className={`p-4 rounded-xl border-2 text-left transition-all ${
                plStatus === 'no' 
                  ? 'border-amber-500 bg-amber-50 ring-2 ring-amber-500/20' 
                  : 'border-gray-200 hover:border-amber-300 bg-white'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${plStatus === 'no' ? 'bg-amber-100' : 'bg-gray-100'}`}>
                  <FileText className={`w-5 h-5 ${plStatus === 'no' ? 'text-amber-600' : 'text-gray-400'}`} />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">No, I need to use alternative documents</p>
                  <p className="text-sm text-gray-500">Bank statements, tax returns, or other records</p>
                </div>
              </div>
            </button>
          </div>

          {/* No P&L Info Banner */}
          {plStatus === 'no' && (
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-blue-600 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-blue-900">No problem! We can reconstruct your P&L</p>
                  <p className="text-sm text-blue-700 mt-1">
                    Our AI-powered system can reconstruct a P&L statement from bank statements, tax returns (Form 1065, K-1), 
                    and other financial records. The reconstructed data is validated against industry benchmarks.
                  </p>
                  <button 
                    onClick={() => setShowDocGuide(true)}
                    className="mt-2 text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                  >
                    <HelpCircle className="w-4 h-4" />
                    Learn more about alternative documentation
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Step 2: Method Selection (only show after P&L status is selected) */}
        {plStatus && (
          <>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium">2</div>
              <h2 className="font-semibold text-gray-900">Choose your input method</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              {methods.map((method) => {
                const isSelected = selectedMethod === method.id;
                const isHovered = hoveredMethod === method.id;
                const colors = getColorClasses(method.color, isSelected, isHovered);
                const recommended = isRecommended(method);

                return (
                  <button
                    key={method.id}
                    onClick={() => setSelectedMethod(method.id)}
                    onMouseEnter={() => setHoveredMethod(method.id)}
                    onMouseLeave={() => setHoveredMethod(null)}
                    className={`
                      relative p-5 rounded-xl border-2 text-left transition-all duration-200
                      ${colors.bg} ${colors.border}
                    `}
                  >
                    {/* Recommended Badge */}
                    {recommended && (
                      <div className="absolute -top-2.5 left-4 px-2 py-0.5 bg-blue-600 text-white text-xs font-medium rounded-full">
                        {plStatus === 'no' ? 'Best for P&L Reconstruction' : 'Recommended'}
                      </div>
                    )}

                    {/* Selection Indicator */}
                    {isSelected && (
                      <div className={`absolute top-4 right-4 w-6 h-6 rounded-full flex items-center justify-center ${colors.icon}`}>
                        <CheckCircle className="w-4 h-4" />
                      </div>
                    )}

                    {/* Icon */}
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${colors.icon}`}>
                      <method.icon className="w-6 h-6" />
                    </div>

                    {/* Title & Subtitle */}
                    <h3 className="font-semibold text-gray-900 mb-1">{method.title}</h3>
                    <p className="text-sm text-gray-500 mb-3">{method.subtitle}</p>

                    {/* Description */}
                    <p className="text-sm text-gray-600 mb-4">
                      {plStatus === 'no' ? method.descriptionNoPL : method.description}
                    </p>

                    {/* Features */}
                    <div className="space-y-2">
                      {method.features.map((feature, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
                          <feature.icon className={`w-4 h-4 ${colors.text}`} />
                          <span>{feature.text}</span>
                        </div>
                      ))}
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {/* Continue Button */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleContinue}
            disabled={!selectedMethod || !plStatus}
            className={`
              flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium transition-all
              ${selectedMethod && plStatus
                ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-600/25' 
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }
            `}
          >
            Continue
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Help Text */}
        <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h4 className="font-medium text-gray-900 mb-2">Need help?</h4>
          <p className="text-sm text-gray-600">
            {plStatus === 'no' ? (
              <>
                <strong>Upload Documents</strong> is the best choice for reconstructing P&L from alternative records. 
                Our AI will automatically categorize transactions and compare against industry benchmarks.{' '}
                <button onClick={() => setShowDocGuide(true)} className="text-blue-600 hover:underline">
                  View documentation guide →
                </button>
              </>
            ) : (
              <>
                <strong>Upload Documents</strong> is best for most users. Our AI will automatically 
                categorize your transactions, detect data quality issues, and reconstruct your P&L 
                statement from raw bank statements or tax documents.
              </>
            )}
          </p>
        </div>

        {/* Document Guide Link */}
        <div className="mt-4 text-center">
          <Link 
            to="/docs-guide" 
            className="text-sm text-gray-500 hover:text-blue-600 flex items-center justify-center gap-1"
          >
            <BookOpen className="w-4 h-4" />
            View complete documentation guide
          </Link>
        </div>
      </div>

      {/* Document Guide Modal */}
      <DocumentGuideModal isOpen={showDocGuide} onClose={() => setShowDocGuide(false)} />
    </AppLayout>
  );
}

// Export the full NewClaim component with P&L question as default
export default NewClaimLegacy;
